import {
  MutationFunction,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  BodyType,
  customInstance,
  ErrorType,
} from '@/features/auth/axios-config'
import { DOCUMENT_LIST_QUERY_KEY } from '@/features/document'
import {
  ErrorResponse,
  RequestApprovalDeliverableSet,
  RequestCancelApprovalRequest,
  RequestCreateDeliverableSet,
  RequestDelegateReview,
  RequestDraftApprovalDeliverableSet,
  RequestUpdateDeliverableSet,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import {
  PROJECT_DETAIL_QUERY_KEY,
  PROJECT_LIST_QUERY_KEY as PROJECT_QUERY_KEY,
} from '../project/constants'

import {
  DELIVERABLE_SET_DETAIL_QUERY_KEY,
  DELIVERABLE_SET_DETAIL_STALE_TIME,
  DELIVERABLE_SET_LIST_QUERY_KEY,
  QUERY_KEY_DELIVERABLE_SET_LIST,
} from './constants'
import { deliverableSetListApi, SecondParameter } from './services'
import { DeliverableSetFilters } from './types'

/**
 * Helper function to invalidate related queries after deliverable set mutations
 * Follows DRY principle to avoid code duplication
 */
const invalidateDeliverableSetQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId?: string,
  deliverableSetId?: string
) => {
  // Always invalidate deliverable set list
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_DELIVERABLE_SET_LIST],
    refetchType: 'active',
  })

  // Invalidate specific deliverable set detail if provided
  if (deliverableSetId) {
    queryClient.invalidateQueries({
      queryKey: DELIVERABLE_SET_DETAIL_QUERY_KEY(deliverableSetId),
      refetchType: 'active',
    })

    // Invalidate document list for this deliverable set
    // Adding a slight delay to ensure backend has processed changes
    setTimeout(
      () =>
        queryClient.invalidateQueries({
          queryKey: DOCUMENT_LIST_QUERY_KEY(),
          refetchType: 'active',
        }),
      1000
    )
  }

  // Invalidate project queries if projectId is provided
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: PROJECT_DETAIL_QUERY_KEY(projectId),
      refetchType: 'active',
    })
    queryClient.invalidateQueries({
      queryKey: PROJECT_QUERY_KEY([]),
      refetchType: 'active',
    })
  }
}

/**
 * Hook to fetch deliverable sets data
 * @param search - Optional search query (minimum 3 characters after trim)
 */
export const useDeliverableSetList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: Partial<DeliverableSetFilters>
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
    enabled = true,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '-created_at'
  )
  const filterBy: Record<string, string[]> = {}
  if (filters?.companyId) {
    filterBy.company_id = [filters.companyId]
  }
  if (filters?.projectId) {
    filterBy.project_id = [filters.projectId]
  }
  if (filters?.status && filters.status.length > 0) {
    filterBy.status = filters.status
  }

  return useQuery({
    queryKey: DELIVERABLE_SET_LIST_QUERY_KEY([
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters || {}),
    ]),
    queryFn: async () => {
      const { data, pagination } =
        await deliverableSetListApi.getDeliverableSetList({
          q: query,
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          sort_by: sortBy,
          filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
        })
      return { data, pagination }
    },
    staleTime: 0,
    enabled,
  })
}

/**
 * Hook to fetch a single deliverable set by ID
 * @param id - Deliverable set ID from URL params
 * @returns React Query hook with deliverable set data
 */
export const useDeliverableSetById = (id: string | undefined) => {
  return useQuery({
    queryKey: DELIVERABLE_SET_DETAIL_QUERY_KEY(id || ''),
    queryFn: async () => {
      if (!id) {
        throw new Error('Deliverable set ID is required')
      }
      const response = await deliverableSetListApi.getDeliverableSetById(id)
      return response.data?.[0]
    },
    enabled: !!id, // Only run query if id is provided
    staleTime: DELIVERABLE_SET_DETAIL_STALE_TIME,
  })
}

/**
 * Hook to create a new deliverable set
 * @returns React Query mutation hook
 */
export const useCreateDeliverableSet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RequestCreateDeliverableSet) =>
      deliverableSetListApi.createDeliverableSet(data),
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id || variables.project_id
      invalidateDeliverableSetQueries(queryClient, projectId)
    },
  })
}

/**
 * Hook to update an existing deliverable set
 * @returns React Query mutation hook
 */
export const useUpdateDeliverableSet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: RequestUpdateDeliverableSet
    }) => deliverableSetListApi.updateDeliverableSet(id, data),
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id
      invalidateDeliverableSetQueries(queryClient, projectId, variables.id)
    },
  })
}

/**
 * Hook to delete a deliverable set
 * @returns React Query mutation hook
 */
export const useDeleteDeliverableSet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; projectId?: string }) =>
      deliverableSetListApi.deleteDeliverableSet(id),
    onSuccess: (_, variables) => {
      invalidateDeliverableSetQueries(queryClient, variables.projectId)
    },
  })
}

export const useSendToReview = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: RequestApprovalDeliverableSet
    }) => deliverableSetListApi.sendToReview(id, data),
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id
      invalidateDeliverableSetQueries(queryClient, projectId, variables.id)
    },
  })
}

export const useCancelApprovalRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: RequestCancelApprovalRequest
    }) => deliverableSetListApi.cancelApprovalRequest(id, data),
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id
      invalidateDeliverableSetQueries(queryClient, projectId, variables.id)
    },
  })
}

export const useDelegateReview = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RequestDelegateReview) =>
      deliverableSetListApi.delegateReview(data),
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id
      invalidateDeliverableSetQueries(
        queryClient,
        projectId,
        variables.deliverable_set_id
      )
    },
  })
}

/**
 * @summary Move deliverable set back to draft from in_review
 */
export const useDraftApprovalRequest = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deliverableSetListApi.draftApprovalRequest>>,
      TError,
      {
        deliverableSetId: string
        data: BodyType<RequestDraftApprovalDeliverableSet>
      },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deliverableSetListApi.draftApprovalRequest>>,
  TError,
  {
    deliverableSetId: string
    data: BodyType<RequestDraftApprovalDeliverableSet>
  },
  TContext
> => {
  const mutationKey = ['useDraftApprovalRequest']
  let resolved
  if (!options) {
    resolved = { mutation: { mutationKey }, request: undefined }
  } else if (
    options.mutation &&
    'mutationKey' in options.mutation &&
    options.mutation.mutationKey
  ) {
    resolved = options
  } else {
    resolved = { ...options, mutation: { ...options.mutation, mutationKey } }
  }
  const { mutation: mutationOptions, request: requestOptions } = resolved

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deliverableSetListApi.draftApprovalRequest>>,
    {
      deliverableSetId: string
      data: BodyType<RequestDraftApprovalDeliverableSet>
    }
  > = (props) => {
    const { deliverableSetId, data } = props ?? {}

    return deliverableSetListApi.draftApprovalRequest(
      deliverableSetId,
      data,
      requestOptions
    )
  }

  return useMutation({ mutationFn, ...mutationOptions }, queryClient)
}
